import { useNavigate } from 'react-router-dom';
import Breadcrumb from '@/components/common/Breadcrumb';
import CSVUpload from '@/components/common/CSVUpload';
import { useToast } from '@/components/common/Toast';
import { ParsedCSVData } from '@/types/upload';
import { countPointItemsByPlatform, validatePointCsv } from '@/utils/pointCsvValidation';

/**
 * プレビュー上部に出すプラットフォーム別の商品件数。
 *
 * 0 件のプラットフォームは、このプランの適用期間中「販売停止」になる（旧プランは継続しない）。
 * 記載漏れはバリデーションで止めない（意図的な販売停止もありうるため）ので、
 * 反映前に気づけるようここで警告色で明示する（docs/features/point.md ADR-008 / ADR-002）。
 */
const PlatformItemCounts = ({ data }: { data: ParsedCSVData }) => {
  const counts = countPointItemsByPlatform(data);
  const missing = counts.filter((c) => c.count === 0);

  return (
    <div className="p-3 bg-white border border-green-200 rounded">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span className="font-semibold text-admin-text">プラットフォーム別 商品件数</span>
        {counts.map(({ platform, count }) => (
          <span
            key={platform}
            className={count === 0 ? 'text-red-600 font-semibold' : 'text-admin-text'}
          >
            {platform}: {count} 件
          </span>
        ))}
      </div>
      {missing.length > 0 && (
        <p className="mt-2 text-sm text-red-600">
          ⚠️ {missing.map((c) => c.platform).join(' / ')}{' '}
          の商品が 1 件もありません。この CSV
          を反映すると、対象プラットフォームはこのプランの適用期間中、購入メニューが空（販売停止）になります。意図した変更か確認してください。
        </p>
      )}
    </div>
  );
};

const PointUpload = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const handleUploadSuccess = (_data: ParsedCSVData) => {
    showToast('アップロードを受け付けました。処理状況はアップロード一覧で確認してください');
    navigate('/upload-management/new');
  };

  return (
    <div>
      <Breadcrumb />
      <div className="card max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold text-admin-text mb-2">
          ポイント（購入プラン）CSVアップロード
        </h1>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h2 className="font-semibold text-admin-text mb-2">
            📋 CSVファイルのフォーマットと注意事項
          </h2>
          <ul className="text-sm text-admin-textLight space-y-1 list-disc list-inside">
            <li>
              ファイル形式：CSV形式（.csv）のみ / サイズ 10MB 以下 / 文字コード UTF-8（BOM
              可）または Shift_JIS
            </li>
            <li>
              <strong>1 ファイル = 1 プラン</strong>の 4 行構成：1 行目＝カラム名 / 2
              行目＝説明（記入ガイド。取込時は読み飛ばす）/ 3 行目＝プラン行 / 4 行目以降＝明細行（1
              行 = 1 商品、最大 300 行）
            </li>
            <li>
              プラン行は plan_name / start_at / end_at / note のみ、明細行は platform / item_name /
              display_name / price / bonus_ecoin / total_ecoin / note のみを記入する
            </li>
            <li>
              日時列（start_at / end_at）は<strong>すべて JST</strong>。形式は 2026-04-01
              00:00（日付のみ・タイムゾーン付きは不可）。end_at 空欄は無期限（後続プランの開始で切替）
            </li>
            <li>
              <strong>end_at は「後続プランなしで販売を終了する」場合にのみ設定</strong>
              する。end_at を入れると、その日時までに後続プランが無い場合そのプラットフォームは
              販売停止（購入メニュー非表示）になり、前のプランには戻りません
            </li>
            <li>
              plan_name は半角英数・ハイフン（-）・アンダースコア（_）の 3〜64 桁。既存プランと同名の
              CSV は<strong>差し替え（上書き更新）</strong>になる
            </li>
            <li>
              platform は web / ios / android（小文字）。iOS / Android の item_name は
              <strong>ストア商品 ID（IAP product id）と一致</strong>させること
            </li>
            <li>
              <strong>明細の記載順がそのまま表示順</strong>
              になる（プラットフォームごとに上から採番。並べ替えは行の移動だけでよい）
            </li>
            <li>
              price は 1 以上 / bonus_ecoin は 0 以上 / total_ecoin は 1 以上かつ bonus_ecoin
              以上（いずれも上限 1,000,000。桁の打ち間違い防止）。通常は total_ecoin = price +
              bonus_ecoin（ストア価格帯調整などの不一致は許容）
            </li>
            <li>
              <strong>常に全プラットフォーム（web / ios / android）のセットで記載</strong>
              すること。商品が 1 件もないプラットフォームはこのプランの適用期間中
              <strong>販売停止</strong>になる（旧プランは継続しない）。
              プレビューの<strong>プラットフォーム別 商品件数</strong>で 0 件が無いか確認すること
            </li>
            <li>1 件でもエラーがあるとその CSV は 1 行も反映されません</li>
          </ul>
        </div>

        <CSVUpload
          uploadType="Point"
          customValidate={validatePointCsv}
          previewSummary={(data) => <PlatformItemCounts data={data} />}
          onUploadSuccess={handleUploadSuccess}
        />

        <div className="flex gap-4 mt-6">
          <button
            onClick={() => navigate('/upload-management/new')}
            className="px-6 py-2 border border-gray-300 text-admin-text rounded-lg hover:bg-gray-50 transition-colors"
          >
            戻る
          </button>
        </div>
      </div>
    </div>
  );
};

export default PointUpload;
